import { Fragment, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardContent,
  Chip,
  Collapse,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
} from "firebase/firestore";
import { useLocation } from "react-router-dom";

import { db } from "../Config/firebase";
import { consultaTenantAtual } from "../Utils/tenant";
import Navbar from "../Components/Navbar/navbar";
import {
  DIMENSOES_PSICOSSOCIAIS,
  calcularDimensoesPsicossociais,
  classificarRiscoMatriz,
  normalizarRespostasPsicossociais,
} from "../Psicossocial/psicossocialUtils";
import {
  formatarPeriodoCiclo,
  getCycleMeta,
  getCycleMetaFromResposta,
  getPeriodoFiltro,
  isDateWithinCycle,
  normalizePeriodicidade,
  toDateFromUnknown,
} from "./ciclo.utils";

function formatarTipo(tipo = "") {
  const mapa = {
    admin: "ADMIN",
    cliente: "CLIENTE",
    colaborador: "COLABORADOR",
    outros: "OUTROS",
  };

  return mapa[tipo?.toLowerCase()] || (tipo || "OUTROS").toUpperCase();
}

function getValorSatisfacao(resposta) {
  if (resposta.indice != null) return Number(resposta.indice);

  const obtida = Number(resposta.pontuacaoObtida ?? 0);
  const maxima = Number(resposta.pontuacaoMaxima ?? 0);
  if (!maxima) return 0;

  return Number(((obtida / maxima) * 100).toFixed(2));
}

function formatarData(createdAt) {
  const data = toDateFromUnknown(createdAt);
  return data ? data.toLocaleString("pt-BR") : "-";
}

function formatarVariacao(valor) {
  if (valor == null) return "-";
  const sinal = valor > 0 ? "+" : "";
  return `${sinal}${valor.toFixed(1)}`;
}

function pertenceAoCicloAtual(resposta) {
  const cicloAtual = getCycleMeta(new Date(), resposta.periodicidadeCiclos);

  if (isDateWithinCycle(resposta.createdAt, cicloAtual)) return true;

  return (
    resposta.cicloMeta?.ano === cicloAtual.ano &&
    resposta.cicloMeta?.cicloNumero === cicloAtual.cicloNumero
  );
}

function resolverPeriodicidadeResposta(data, kpi = {}) {
  const periodicidadeKpi = Number(kpi.periodicidadeCiclos);
  const periodicidadeResposta = Number(data.periodicidadeCiclos);
  const trimestreLegado = Number(data.trimestre);

  if (Number.isFinite(periodicidadeKpi) && periodicidadeKpi > 0) {
    return periodicidadeKpi;
  }

  if (Number.isFinite(periodicidadeResposta) && periodicidadeResposta > 0) {
    return periodicidadeResposta;
  }

  // Registros antigos desta pesquisa usavam `trimestre` como campo oficial do ciclo.
  if (Number.isFinite(trimestreLegado) && trimestreLegado >= 1 && trimestreLegado <= 4) {
    return 4;
  }

  return 4;
}

function resolverCicloMetaResposta(data, periodicidade) {
  const cicloPelaDataResposta = getCycleMeta(data.createdAt, periodicidade);

  if (data.createdAt) {
    return cicloPelaDataResposta;
  }

  const cicloMetaSalvo = getCycleMetaFromResposta({
    createdAt: data.createdAt,
    ciclo: data.ciclo,
    ano: data.ano,
    cicloNumero: data.cicloNumero ?? data.trimestre,
    periodicidade,
  });

  if (!data.createdAt || isDateWithinCycle(data.createdAt, cicloMetaSalvo)) {
    return cicloMetaSalvo;
  }

  return cicloPelaDataResposta;
}

function renderTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;

  const media = payload.find((item) => item.dataKey === "media");
  const variacao = payload.find((item) => item.dataKey === "variacao");
  const metaEsperada = payload.find((item) => item.dataKey === "metaEsperada");
  const valorMetaEsperada = metaEsperada?.value ?? payload[0]?.payload?.metaEsperada;
  const respostas = payload[0]?.payload?.total ?? 0;

  return (
    <Box
      sx={{
        backgroundColor: "#fff",
        border: "1px solid #d0d7de",
        borderRadius: 1,
        p: 1.5,
      }}
    >
      <Typography fontWeight={700} mb={0.5}>
        {label}
      </Typography>
      <Typography variant="body2">{payload[0]?.payload?.periodoCompleto ?? "-"}</Typography>
      <Typography variant="body2">Média: {media?.value?.toFixed?.(1) ?? "-"}</Typography>
      <Typography variant="body2">
        Meta esperada: {valorMetaEsperada?.toFixed?.(1) ?? "-"}
      </Typography>
      <Typography variant="body2">Respostas: {respostas}</Typography>
      <Typography variant="body2">
        Variação: {variacao?.value == null ? "-" : formatarVariacao(variacao.value)}
      </Typography>
    </Box>
  );
}

export default function AdminKpiRespostas() {
  const location = useLocation();
  const kpiIdInicial =
    new URLSearchParams(location.search).get("kpiId") || "";
  const [respostas, setRespostas] = useState([]);
  const [modoVisualizacao, setModoVisualizacao] = useState("ciclo");
  const [filtroKpi, setFiltroKpi] = useState(kpiIdInicial);
  const [periodoSelecionadoId, setPeriodoSelecionadoId] = useState("");
  const [loading, setLoading] = useState(true);
  const [linhaAberta, setLinhaAberta] = useState(null);
  const [detalhesMap, setDetalhesMap] = useState({});
  const [filtroCliente, setFiltroCliente] = useState("");

  async function carregarDetalhes(resposta) {
    if (detalhesMap[resposta.id]) return;

    const perguntasSnap = await getDocs(
      consultaTenantAtual(collection(db, "kpi_perguntas"))
    );

    const perguntas = perguntasSnap.docs
      .map((item) => ({ id: item.id, ...item.data() }))
      .filter((pergunta) => pergunta.kpiId === resposta.kpiId && pergunta.ativa === true)
      .sort((a, b) => Number(a.ordem || 0) - Number(b.ordem || 0));

    const respSnap = await getDoc(doc(db, "kpi_respostas", resposta.id));
    const respostasUsuario = respSnap.exists() ? respSnap.data().respostas || {} : {};

    setDetalhesMap((prev) => ({
      ...prev,
      [resposta.id]: { perguntas, respostas: respostasUsuario },
    }));
  }

 useEffect(() => {
  const q = consultaTenantAtual(collection(db, "kpi_respostas"));

  const unsubscribe = onSnapshot(
    q,
    async (snap) => {
      try {
        const userCache = new Map();
        const kpiCache = new Map();

        const lista = await Promise.all(
          snap.docs.map(async (docResp) => {
            const data = docResp.data();

            let usuario = {};

            if (data.userId) {
              if (!userCache.has(data.userId)) {
                userCache.set(
                  data.userId,
                  getDoc(doc(db, "users", data.userId)).catch((error) => {
                    console.warn(
                      "Não foi possível carregar o usuário da resposta:",
                      data.userId,
                      error
                    );

                    return null;
                  })
                );
              }

              const userSnap = await userCache.get(data.userId);

              if (userSnap?.exists()) {
                usuario = userSnap.data();
              }
            }

            let kpi = {};

            if (data.kpiId) {
              if (!kpiCache.has(data.kpiId)) {
                kpiCache.set(
                  data.kpiId,
                  getDoc(doc(db, "kpis", data.kpiId)).catch((error) => {
                    console.warn(
                      "Não foi possível carregar o KPI da resposta:",
                      data.kpiId,
                      error
                    );

                    return null;
                  })
                );
              }

              const kpiSnap = await kpiCache.get(data.kpiId);

              if (kpiSnap?.exists()) {
                kpi = kpiSnap.data();
              }
            }

            const periodicidade = normalizePeriodicidade(
              resolverPeriodicidadeResposta(data, kpi)
            );

            const cicloMeta = resolverCicloMetaResposta(
              data,
              periodicidade
            );

            const periodoCiclo = formatarPeriodoCiclo(
              cicloMeta.inicio,
              cicloMeta.fim
            );

            const tipoFormulario =
              data.tipoFormulario ||
              kpi.tipoFormulario ||
              "GERAL";

            const respostasRaw =
              tipoFormulario === "PSICOSSOCIAL"
                ? normalizarRespostasPsicossociais(
                    data.respostas || {},
                    data.versaoPontuacaoPsicossocial
                  )
                : data.respostas || {};

            return {
              id: docResp.id,
              usuarioNome:
                usuario.nome ||
                data.usuarioNome ||
                data.colaboradorNome ||
                "-",
              tipoUsuario: (
                usuario.tipo ||
                data.tipoUsuario ||
                "outros"
              ).toLowerCase(),
              obraNome: data.obraNome || "-",
              kpiId: data.kpiId,
              kpiNome: kpi.nome || data.kpiNome || "-",
              kpiRegra: kpi.regra || null,
              tipoFormulario,
              clienteId: data.clienteId || "",
              clienteNome: data.clienteNome || "-",
              dataNascimento: data.dataNascimento || "",
              setor: data.setor || "",
              funcao: data.funcao || "",
              respostasRaw,
              periodicidadeCiclos:
  Number(kpi.periodicidadeCiclos) > 0
    ? Number(kpi.periodicidadeCiclos)
    : periodicidade,
              indice: data.indice ?? null,
              pontuacaoObtida: data.pontuacaoObtida ?? 0,
              pontuacaoMaxima: data.pontuacaoMaxima ?? 100,
              createdAt: data.createdAt,
              cicloMeta,
              periodoCiclo,
              comparativoCicloId: `${cicloMeta.ano}-C${cicloMeta.cicloNumero}`,
              comparativoAnoId: String(cicloMeta.ano),
              satisfacao: getValorSatisfacao(data),
            };
          })
        );

        lista.sort((a, b) => {
          const dataB = b.createdAt?.seconds || 0;
          const dataA = a.createdAt?.seconds || 0;

          return dataB - dataA;
        });

        setRespostas(lista);
      } catch (error) {
        console.error(
          "Erro ao processar respostas dos KPIs:",
          error
        );
      } finally {
        setLoading(false);
      }
    },
    (error) => {
      console.error(
        "Erro ao consultar kpi_respostas:",
        error
      );

      setLoading(false);
    }
  );

  return () => unsubscribe();
}, []);

  const kpisDisponiveis = useMemo(() => {
    const map = new Map();

    respostas.forEach((resposta) => {
      if (resposta.kpiId) map.set(resposta.kpiId, resposta.kpiNome);
    });

    return Array.from(map.entries());
  }, [respostas]);

 const kpisPorId = useMemo(() => {
  const map = new Map();

  respostas.forEach((resposta) => {
    if (!resposta.kpiId || map.has(resposta.kpiId)) return;

    map.set(resposta.kpiId, {
      id: resposta.kpiId,
      nome: resposta.kpiNome,
      regra: resposta.kpiRegra,
      periodicidadeCiclos: resposta.periodicidadeCiclos,
      tipoFormulario: resposta.tipoFormulario,
    });
  });

  return map;
}, [respostas]);

  const kpiSelecionado = useMemo(() => {
    if (!filtroKpi) return null;
    return kpisPorId.get(filtroKpi) || null;
  }, [filtroKpi, kpisPorId]);

  const isPsicossocialSelecionado = kpiSelecionado?.tipoFormulario === "PSICOSSOCIAL";

  const clientesDisponiveis = useMemo(() => {
    const map = new Map();

    respostas.forEach((resposta) => {
      if (resposta.clienteId) map.set(resposta.clienteId, resposta.clienteNome || "Empresa sem nome");
    });

    return Array.from(map.entries()).sort((a, b) => a[1].localeCompare(b[1], "pt-BR"));
  }, [respostas]);

  const periodicidadeSelecionada = useMemo(
    () => normalizePeriodicidade(kpiSelecionado?.periodicidadeCiclos),
    [kpiSelecionado]
  );
console.log("KPI selecionado:", kpiSelecionado);
console.log("Periodicidade usada:", periodicidadeSelecionada);
  const metaEsperadaSelecionada = useMemo(() => {
    const valorMeta = kpiSelecionado?.regra?.valor;
    if (valorMeta == null || valorMeta === "") return null;

    const valor = Number(valorMeta);
    return Number.isFinite(valor) ? valor : null;
  }, [kpiSelecionado]);

  useEffect(() => {
    if (filtroKpi || !kpisDisponiveis.length) return;

    const kpiPadrao =
      kpisDisponiveis.find(([, nome]) => nome === "Pesquisa de Satisfação interna") ||
      kpisDisponiveis.find(([, nome]) => nome?.toLowerCase?.() === "pesquisa de satisfação interna") ||
      kpisDisponiveis.find(([, nome]) => nome?.toLowerCase?.() === "pesquisa de satisfacao interna");

    if (kpiPadrao) {
      setFiltroKpi(kpiPadrao[0]);
    }
  }, [filtroKpi, kpisDisponiveis]);

  const respostasFiltradas = useMemo(() => {
    return respostas.filter((resposta) => {
      if (filtroKpi && resposta.kpiId !== filtroKpi) return false;
      if (isPsicossocialSelecionado && filtroCliente && resposta.clienteId !== filtroCliente) return false;
      return true;
    });
  }, [filtroCliente, filtroKpi, isPsicossocialSelecionado, respostas]);

  useEffect(() => {
    if (!isPsicossocialSelecionado) setFiltroCliente("");
  }, [isPsicossocialSelecionado]);

  const respostasAnoAtual = useMemo(() => {
    const anoAtual = new Date().getFullYear();

    return respostasFiltradas.filter((resposta) => {
      const data = toDateFromUnknown(resposta.createdAt);
      return (resposta.cicloMeta?.ano || data?.getFullYear()) === anoAtual;
    });
  }, [respostasFiltradas]);

  const respostasCicloAtual = useMemo(
    () => respostasAnoAtual.filter((resposta) => pertenceAoCicloAtual(resposta)),
    [respostasAnoAtual]
  );

  const resumoCicloAtual = useMemo(() => {
    const cicloAtual = getCycleMeta(new Date(), periodicidadeSelecionada);
    return formatarPeriodoCiclo(cicloAtual.inicio, cicloAtual.fim);
  }, [periodicidadeSelecionada]);

  const resumoAnual = useMemo(() => {
    const anoAtual = new Date().getFullYear();
    return `01/01/${anoAtual} a 31/12/${anoAtual}`;
  }, []);

  const nomeKpiSelecionado = useMemo(() => {
    if (!filtroKpi) return "Pesquisa de Satisfação interna";

    return (
      respostas.find((item) => item.kpiId === filtroKpi)?.kpiNome ||
      kpisDisponiveis.find(([id]) => id === filtroKpi)?.[1] ||
      "Pesquisa selecionada"
    );
  }, [filtroKpi, kpisDisponiveis, respostas]);

  const historicoPorCiclo = useMemo(() => {
    const anoAtual = new Date().getFullYear();
    const totalCiclosGrafico = periodicidadeSelecionada;
    const grupos = new Map();

    respostasAnoAtual.forEach((resposta) => {
      const ano = resposta.cicloMeta?.ano || anoAtual;
      const cicloNumero = resposta.cicloMeta?.cicloNumero;

      if (
        ano !== anoAtual ||
        !cicloNumero ||
        cicloNumero < 1 ||
        cicloNumero > periodicidadeSelecionada
      ) {
        return;
      }

      const key = `${ano}-C${cicloNumero}`;

      if (!grupos.has(key)) {
        grupos.set(key, {
          id: key,
          total: 0,
          soma: 0,
          ano,
          cicloNumero,
        });
      }

      const grupo = grupos.get(key);
      grupo.total += 1;
      grupo.soma += resposta.satisfacao;
    });

    return Array.from({ length: totalCiclosGrafico }, (_, index) => {
      const cicloNumero = index + 1;
      const periodoConfigurado = cicloNumero <= periodicidadeSelecionada;
      const { inicio, fim } = periodoConfigurado
        ? getPeriodoFiltro(periodicidadeSelecionada, anoAtual, cicloNumero)
        : { inicio: null, fim: null };
      const grupo = grupos.get(`${anoAtual}-C${cicloNumero}`);
      const media = grupo?.total ? Number((grupo.soma / grupo.total).toFixed(2)) : null;

      return {
        id: `${anoAtual}-C${cicloNumero}`,
        nome: `C${cicloNumero}`,
        inicio,
        fim,
        periodoCompleto: periodoConfigurado ? formatarPeriodoCiclo(inicio, fim) : "-",
        total: grupo?.total || 0,
        media,
        metaEsperada: metaEsperadaSelecionada,
      };
    }).map((grupo, index, lista) => {
      const anterior = [...lista]
        .slice(0, index)
        .reverse()
        .find((item) => item.media != null);

      return {
        ...grupo,
        variacao:
          anterior && grupo.media != null
            ? Number((grupo.media - anterior.media).toFixed(2))
            : null,
      };
    });
  }, [metaEsperadaSelecionada, periodicidadeSelecionada, respostasAnoAtual]);

  const comparativoAnual = useMemo(() => {
    const grupos = new Map();

    respostasFiltradas.forEach((resposta) => {
      const data = toDateFromUnknown(resposta.createdAt);
      const ano = resposta.cicloMeta?.ano || data?.getFullYear();
      if (!ano) return;

      if (!grupos.has(ano)) {
        grupos.set(ano, {
          id: String(ano),
          nome: String(ano),
          inicio: new Date(ano, 0, 1, 0, 0, 0, 0),
          fim: new Date(ano, 11, 31, 23, 59, 59, 999),
          periodoCompleto: `01/01/${ano} a 31/12/${ano}`,
          total: 0,
          soma: 0,
          ano,
        });
      }

      const grupo = grupos.get(ano);
      grupo.total += 1;
      grupo.soma += resposta.satisfacao;
    });

    return Array.from(grupos.values())
      .sort((a, b) => a.ano - b.ano)
      .map((grupo) => ({
        ...grupo,
        media: grupo.total ? Number((grupo.soma / grupo.total).toFixed(2)) : null,
        metaEsperada: metaEsperadaSelecionada,
      }))
      .map((grupo, index, lista) => {
        const anterior = index > 0 ? lista[index - 1] : null;

        return {
          ...grupo,
          variacao:
            anterior && grupo.media != null && anterior.media != null
              ? Number((grupo.media - anterior.media).toFixed(2))
              : null,
        };
      });
  }, [metaEsperadaSelecionada, respostasFiltradas]);

  const dadosComparativo = modoVisualizacao === "anual" ? comparativoAnual : historicoPorCiclo;

  useEffect(() => {
    if (!dadosComparativo.length) {
      setPeriodoSelecionadoId("");
      return;
    }

    const anoAtual = new Date().getFullYear();
    const periodoPadraoId =
      modoVisualizacao === "anual"
        ? String(anoAtual)
        : `${anoAtual}-C${getCycleMeta(new Date(), periodicidadeSelecionada).cicloNumero}`;

    const periodoExiste = dadosComparativo.some((item) => item.id === periodoSelecionadoId);

    if (periodoExiste) return;

    const periodoPadrao =
      dadosComparativo.find((item) => item.id === periodoPadraoId) || dadosComparativo[0];

    setPeriodoSelecionadoId(periodoPadrao?.id || "");
  }, [dadosComparativo, modoVisualizacao, periodoSelecionadoId, periodicidadeSelecionada]);

  const periodoSelecionado = useMemo(
    () => dadosComparativo.find((item) => item.id === periodoSelecionadoId) || null,
    [dadosComparativo, periodoSelecionadoId]
  );

  const respostasExibidas = useMemo(() => {
    if (!periodoSelecionado) {
      return modoVisualizacao === "anual" ? respostasAnoAtual : respostasCicloAtual;
    }

    if (modoVisualizacao === "anual") {
      return respostasFiltradas.filter((resposta) => {
        const data = toDateFromUnknown(resposta.createdAt);
        if (!data || !periodoSelecionado.inicio || !periodoSelecionado.fim) return false;

        return data >= periodoSelecionado.inicio && data <= periodoSelecionado.fim;
      });
    }

    return respostasAnoAtual.filter((resposta) => {
      const data = toDateFromUnknown(resposta.createdAt);
      if (!data || !periodoSelecionado.inicio || !periodoSelecionado.fim) return false;

      return data >= periodoSelecionado.inicio && data <= periodoSelecionado.fim;
    });
  }, [
    modoVisualizacao,
    periodoSelecionado,
    respostasAnoAtual,
    respostasCicloAtual,
    respostasFiltradas,
  ]);

  const respostasPorTipo = useMemo(() => {
    return respostasExibidas.reduce((acc, resposta) => {
      const tipo = resposta.tipoUsuario || "outros";
      if (!acc[tipo]) acc[tipo] = [];
      acc[tipo].push(resposta);
      return acc;
    }, {});
  }, [respostasExibidas]);

  const mediaAtual = useMemo(() => {
    if (!respostasExibidas.length) return 0;

    const soma = respostasExibidas.reduce((total, resposta) => total + resposta.satisfacao, 0);
    return soma / respostasExibidas.length;
  }, [respostasExibidas]);

  const mediaHistorica = useMemo(() => {
    const origem = modoVisualizacao === "anual" ? comparativoAnual : historicoPorCiclo;
    const itensComMedia = origem.filter((item) => item.media != null);
    if (!itensComMedia.length) return 0;

    const soma = itensComMedia.reduce((total, item) => total + item.media, 0);
    return soma / itensComMedia.length;
  }, [comparativoAnual, historicoPorCiclo, modoVisualizacao]);

  const resumoPsicossocial = useMemo(() => {
    if (!isPsicossocialSelecionado) return [];

    return DIMENSOES_PSICOSSOCIAIS.map((dimensao) => {
      const valores = respostasExibidas
        .map((resposta) =>
          calcularDimensoesPsicossociais(resposta.respostasRaw || {}).find((item) => item.id === dimensao.id)?.percentual
        )
        .filter((valor) => valor != null);

      const media = valores.length
        ? valores.reduce((soma, valor) => soma + valor, 0) / valores.length
        : 0;

      return {
        nome: dimensao.nome,
        media: Number(media.toFixed(1)),
      };
    });
  }, [isPsicossocialSelecionado, respostasExibidas]);

  const riscoPsicossocial = classificarRiscoMatriz(Math.round(mediaAtual) * 5);

  const descricaoPeriodoSelecionado =
    periodoSelecionado?.periodoCompleto ||
    (modoVisualizacao === "anual" ? resumoAnual : resumoCicloAtual);

  return (
    <>
      <Navbar />

      <Box sx={{ p: 3, minHeight: "100vh", backgroundColor: "#f4f6f8" }}>
        <Card>
          <CardContent>
            <Typography variant="h5" fontWeight={700} mb={3}>
              Pesquisas Respondidas
            </Typography>

            <Stack direction={{ xs: "column", md: "row" }} spacing={2} mb={3}>
              <FormControl size="small" sx={{ minWidth: 220 }}>
                <InputLabel>Visualização</InputLabel>
                <Select
                  value={modoVisualizacao}
                  label="Visualização"
                  onChange={(event) => setModoVisualizacao(event.target.value)}
                >
                  <MenuItem value="ciclo">Por ciclo</MenuItem>
                  <MenuItem value="anual">Anual</MenuItem>
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 260 }}>
                <InputLabel>KPI</InputLabel>
                <Select
                  value={filtroKpi}
                  label="KPI"
                  onChange={(event) => setFiltroKpi(event.target.value)}
                >
                  {kpisDisponiveis
  .filter(([id]) => kpisPorId.get(id)?.tipoFormulario !== "PSICOSSOCIAL")
  .map(([id, nome]) => (
    <MenuItem key={id} value={id}>
      {nome}
    </MenuItem>
  ))}
                </Select>
              </FormControl>

              {isPsicossocialSelecionado && (
                <FormControl size="small" sx={{ minWidth: 260 }}>
                  <InputLabel>Empresa</InputLabel>
                  <Select
                    value={filtroCliente}
                    label="Empresa"
                    onChange={(event) => setFiltroCliente(event.target.value)}
                  >
                    <MenuItem value="">Todas</MenuItem>
                    {clientesDisponiveis.map(([id, nome]) => (
                      <MenuItem key={id} value={id}>
                        {nome}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Stack>

            {loading && <Typography align="center">Carregando...</Typography>}

            {!loading && (
              <>
                <Box
                  sx={{
                    display: "grid",
                    gap: 2,
                    gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
                    mb: 3,
                  }}
                >
                  <Card variant="outlined">
                    <CardContent>
                      <Typography color="text.secondary" variant="body2" mb={1}>
                        {modoVisualizacao === "anual" ? "Período anual" : "Ciclo atual"}
                      </Typography>
                      <Typography fontWeight={700}>
                        {modoVisualizacao === "anual"
                          ? `Anual: ${descricaoPeriodoSelecionado}`
                          : resumoCicloAtual === "-"
                            ? "-"
                            : `Período selecionado: ${descricaoPeriodoSelecionado}`}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {respostasExibidas.length} respostas no período selecionado
                      </Typography>
                    </CardContent>
                  </Card>

                  <Card variant="outlined">
                    <CardContent>
                      <Typography color="text.secondary" variant="body2" mb={1}>
                        {modoVisualizacao === "anual" ? "Média anual" : "Média do ciclo atual"}
                      </Typography>
                      <Typography fontWeight={700}>{mediaAtual.toFixed(1)}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {modoVisualizacao === "anual"
                          ? "Considera todas as respostas do ano atual"
                          : "Considera as respostas do ciclo selecionado"}
                      </Typography>
                    </CardContent>
                  </Card>

                  <Card variant="outlined">
                    <CardContent>
                      <Typography color="text.secondary" variant="body2" mb={1}>
                        {isPsicossocialSelecionado ? "Classificacao geral" : "Media historica"}
                      </Typography>
                      <Typography fontWeight={700}>
                        {isPsicossocialSelecionado ? riscoPsicossocial.label : mediaHistorica.toFixed(1)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {isPsicossocialSelecionado
                          ? "Considera as respostas psicossociais filtradas"
                          : "Media dos ciclos ja concluidos"}
                      </Typography>
                    </CardContent>
                  </Card>
                </Box>

                {isPsicossocialSelecionado && (
                  <Card variant="outlined" sx={{ mb: 3 }}>
                    <CardContent>
                      <Typography variant="h6" fontWeight={700} mb={0.5}>
                        KPI Psicossocial por dimensao
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mb={2}>
                        O grafico consolida as respostas dos colaboradores da empresa selecionada.
                      </Typography>

                      <Box sx={{ width: "100%", height: 340 }}>
                        <ResponsiveContainer>
                          <ComposedChart data={resumoPsicossocial}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="nome" tick={{ fontSize: 11 }} interval={0} angle={-20} textAnchor="end" height={90} />
                            <YAxis domain={[0, 100]} ticks={[0, 20, 40, 60, 80, 100]} />
                            <Tooltip />
                            <Bar dataKey="media" name="Media %" fill="#1976d2" radius={[6, 6, 0, 0]} />
                          </ComposedChart>
                        </ResponsiveContainer>
                      </Box>
                    </CardContent>
                  </Card>
                )}

                <Card variant="outlined" sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h6" fontWeight={700} mb={0.5}>
                      {modoVisualizacao === "anual"
                        ? `Comparativo anual - ${nomeKpiSelecionado}`
                        : `Comparativo entre os ciclos do ano - ${nomeKpiSelecionado}`}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                      {modoVisualizacao === "anual"
                        ? "O gráfico mostra a média anual de satisfação por ano, com a diferença em relação ao ano anterior."
                        : "O gráfico mostra os ciclos configurados no cadastro do KPI, incluindo ciclos parciais, com a diferença em relação ao ciclo anterior."}
                    </Typography>

                    {!dadosComparativo.some((item) => item.media != null) ? (
                      <Alert severity="info">
                        Ainda não há respostas suficientes para montar o comparativo.
                      </Alert>
                    ) : (
                      <>
                        <Box sx={{ width: "100%", height: 320, mb: 2 }}>
                          <ResponsiveContainer>
                            <ComposedChart data={dadosComparativo}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="nome" />
                              <YAxis yAxisId="left" domain={[0, 100]} ticks={[0, 20, 40, 60, 80, 100]} />
                              <Tooltip content={renderTooltip} />
                              {metaEsperadaSelecionada != null && (
                                <ReferenceLine
                                  yAxisId="left"
                                  y={metaEsperadaSelecionada}
                                  name="Meta esperada"
                                  stroke="#d32f2f"
                                  strokeWidth={2}
                                  strokeDasharray="4 4"
                                />
                              )}
                              <Bar
                                yAxisId="left"
                                dataKey="media"
                                name={modoVisualizacao === "anual" ? "Média anual" : "Média de satisfação"}
                                fill="#1976d2"
                                radius={[6, 6, 0, 0]}
                              />
                            </ComposedChart>
                          </ResponsiveContainer>
                        </Box>

                        <Stack
                          direction="row"
                          spacing={2}
                          justifyContent="center"
                          alignItems="center"
                          sx={{ mb: 2, color: "text.secondary", fontSize: 14 }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                            <Box sx={{ width: 12, height: 12, backgroundColor: "#1976d2" }} />
                            <span>
                              {modoVisualizacao === "anual"
                                ? "Média anual"
                                : "Média de satisfação"}
                            </span>
                          </Box>

                          {metaEsperadaSelecionada != null && (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                              <Box
                                sx={{
                                  width: 24,
                                  borderTop: "2px dashed #d32f2f",
                                }}
                              />
                              <span>Meta esperada</span>
                            </Box>
                          )}
                        </Stack>

                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>
                                <strong>{modoVisualizacao === "anual" ? "Ano" : "Ciclo"}</strong>
                              </TableCell>
                              <TableCell>
                                <strong>Período</strong>
                              </TableCell>
                              <TableCell>
                                <strong>Respostas</strong>
                              </TableCell>
                              <TableCell>
                                <strong>Média</strong>
                              </TableCell>
                              <TableCell>
                                <strong>Diferença</strong>
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {dadosComparativo.map((item) => (
                              <TableRow
                                key={item.id}
                                hover
                                selected={item.id === periodoSelecionadoId}
                                onClick={() => setPeriodoSelecionadoId(item.id)}
                                sx={{ cursor: "pointer" }}
                              >
                                <TableCell>{item.nome}</TableCell>
                                <TableCell>{item.periodoCompleto}</TableCell>
                                <TableCell>{item.total}</TableCell>
                                <TableCell>
                                  {item.media == null ? "-" : item.media.toFixed(1)}
                                </TableCell>
                                <TableCell>{formatarVariacao(item.variacao)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </>
                    )}
                  </CardContent>
                </Card>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="h6" fontWeight={700} mb={0.5}>
                    {modoVisualizacao === "anual"
                      ? "Respostas do ano selecionado"
                      : "Respostas do ciclo selecionado"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {descricaoPeriodoSelecionado
                      ? `Mostrando as respostas do período ${descricaoPeriodoSelecionado}.`
                      : "Mostrando as respostas do período selecionado."}
                  </Typography>
                </Box>

                {!Object.keys(respostasPorTipo).length && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Nenhuma resposta encontrada para o período selecionado.
                  </Alert>
                )}

                {Object.entries(respostasPorTipo).map(([tipo, lista]) => {
                  const total = lista.length;
                  const media =
                    total === 0
                      ? 0
                      : lista.reduce((soma, resposta) => soma + resposta.satisfacao, 0) / total;

                  const isColaborador = tipo === "colaborador";
                  const isCliente = tipo === "cliente";

                  return (
                    <Accordion key={tipo} sx={{ mb: 2 }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            width: "100%",
                            gap: 2,
                          }}
                        >
                          <Typography
                            fontWeight={700}
                            textTransform="uppercase"
                            sx={{ minWidth: 160 }}
                          >
                            {formatarTipo(tipo)}
                          </Typography>

                          <Stack direction="row" spacing={1}>
                            <Chip label={`Respostas: ${total}`} size="small" variant="outlined" />
                            <Chip
                              label={`Média: ${media.toFixed(1)}`}
                              size="small"
                              color="primary"
                            />
                          </Stack>

                          <Box sx={{ flexGrow: 1 }} />
                        </Box>
                      </AccordionSummary>

                      <AccordionDetails>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>
                                <strong>{isCliente ? "Cliente / Obra" : "Usuário"}</strong>
                              </TableCell>
                              {isPsicossocialSelecionado && (
                                <TableCell>
                                  <strong>Empresa</strong>
                                </TableCell>
                              )}
                              {isPsicossocialSelecionado && (
                                <TableCell>
                                  <strong>Setor / Função</strong>
                                </TableCell>
                              )}
                              <TableCell>
                                <strong>KPI</strong>
                              </TableCell>
                              {!isColaborador && !isCliente && (
                                <TableCell>
                                  <strong>Obra</strong>
                                </TableCell>
                              )}
                              <TableCell>
                                  <strong>Satisfação</strong>
                              </TableCell>
                              <TableCell>
                                <strong>Data</strong>
                              </TableCell>
                              <TableCell>
                                  <strong>Período do ciclo</strong>
                              </TableCell>
                            </TableRow>
                          </TableHead>

                          <TableBody>
                            {lista.map((resposta) => {
                              const aberta = linhaAberta === resposta.id;
                              const detalhes = detalhesMap[resposta.id];

                              return (
                                <Fragment key={resposta.id}>
                                  <TableRow
                                    hover
                                    sx={{ cursor: "pointer" }}
                                    onClick={async (event) => {
                                      event.stopPropagation();
                                      setLinhaAberta(aberta ? null : resposta.id);
                                      if (!aberta) await carregarDetalhes(resposta);
                                    }}
                                  >
                                    <TableCell>
                                      {isCliente ? (
                                        <>
                                          <Typography component="div" variant="caption" sx={{ color: "#0f2f5f", fontWeight: 700, fontSize: "0.72rem" }}>
                                            {resposta.clienteNome || "-"}
                                          </Typography>
                                          <Typography component="div" variant="caption" sx={{ color: "#475569", fontWeight: 600, fontSize: "0.7rem" }}>
                                            Obra: {resposta.obraNome || "-"}
                                          </Typography>
                                        </>
                                      ) : (
                                        resposta.usuarioNome
                                      )}
                                    </TableCell>
                                    {isPsicossocialSelecionado && (
                                      <TableCell>{resposta.clienteNome}</TableCell>
                                    )}
                                    {isPsicossocialSelecionado && (
                                      <TableCell>
                                        <Typography variant="body2">{resposta.setor || "-"}</Typography>
                                        <Typography variant="body2" color="text.secondary">
                                          {resposta.funcao || "-"}
                                        </Typography>
                                      </TableCell>
                                    )}
                                    <TableCell>{resposta.kpiNome}</TableCell>

                                    {!isColaborador && !isCliente && (
                                      <TableCell>{resposta.obraNome}</TableCell>
                                    )}

                                    <TableCell>{resposta.satisfacao.toFixed(1)}%</TableCell>
                                    <TableCell>{formatarData(resposta.createdAt)}</TableCell>
                                    <TableCell>{resposta.periodoCiclo || "-"}</TableCell>
                                  </TableRow>

                                  <TableRow>
                                    <TableCell colSpan={6 + (!isColaborador && !isCliente ? 1 : 0) + (isPsicossocialSelecionado ? 2 : 0)} sx={{ p: 0 }}>
                                      <Collapse in={aberta} timeout="auto" unmountOnExit>
                                        <Box sx={{ p: 2, backgroundColor: "#fafafa" }}>
                                          {!detalhes && (
                                            <Typography color="text.secondary">
                                              Carregando respostas...
                                            </Typography>
                                          )}

                                          {detalhes && (
                                            <Table size="small">
                                              <TableBody>
                                                {detalhes.perguntas.map((pergunta) => (
                                                  <TableRow key={pergunta.id}>
                                                    <TableCell width="70%">
                                                      {pergunta.texto}
                                                    </TableCell>
                                                    <TableCell>
                                                      {detalhes.respostas[pergunta.id] ?? detalhes.respostas[pergunta.codigo] ?? "-"}
                                                    </TableCell>
                                                  </TableRow>
                                                ))}
                                              </TableBody>
                                            </Table>
                                          )}
                                        </Box>
                                      </Collapse>
                                    </TableCell>
                                  </TableRow>
                                </Fragment>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              </>
            )}
          </CardContent>
        </Card>
      </Box>
    </>
  );
}
